import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  useMediaQuery,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import {
  PurchaseRequest,
  AssetRequest,
  RevaluationRequest,
  ContributionRequest,
  SellRequest
} from './types';
import { getStatusConfig } from './utils';

interface RequestDialogProps {
  open: boolean;
  request:
    | PurchaseRequest
    | AssetRequest
    | RevaluationRequest
    | ContributionRequest
    | SellRequest
    | null;
  onClose: () => void;
}

const RequestDialog: React.FC<RequestDialogProps> = ({ open, request, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (!request) return null;

  // Enhanced date formatting function
  const formatDateTime = (dateString: string | undefined): string => {
    if (!dateString) return 'Not provided';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      return date.toLocaleDateString('en-AU', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Australia/Sydney'
      });
    } catch {
      return dateString;
    }
  };

  // Format simple dates (YYYY-MM-DD)
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return 'Not provided';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      return date.toLocaleDateString('en-AU', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Format currency values
  const formatCurrency = (value: string | number): string => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return value.toString();
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(numValue);
  };

  // Format percentage values
  const formatPercentage = (value: string): string => {
    if (value.includes('%')) return value;
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value;
    return `${numValue}%`;
  };

  // Clean up field names for better display
  const formatFieldName = (key: string): string => {
    const fieldNameMappings: { [key: string]: string } = {
      requestId: 'Request ID',
      groupId: 'Group ID',
      serviceId: 'Service ID',
      unitType: 'Unit Type',
      assetId: 'Asset ID',
      subType: 'Sub Type',
      purchaseValue: 'Purchase Value',
      expectedAnnualYield: 'Expected Annual Yield',
      cashUsed: 'Cash Used',
      netMonthlyIncome: 'Net Monthly Income',
      totalGrossIncome: 'Total Gross Income',
      totalExpenses: 'Total Expenses',
      createdAt: 'Created Date',
      createdDate: 'Created Date',
      createdBy: 'Created By',
      lastUpdatedDateTime: 'Last Updated Date',
      lastUpdatedDate: 'Last Updated Date',
      lastUpdatedBy: 'Last Updated By',
      purchaseDate: 'Purchase Date',
      fundManager: 'Fund Manager',
      currentValue: 'Current Value',
      maturityDate: 'Maturity Date',
      leaseExpiry: 'Lease Expiry Date',
      dividendFrequency: 'Dividend Frequency',
      paymentFrequency: 'Payment Frequency',
      holdingPeriod: 'Holding Period',
      exitStrategy: 'Exit Strategy',
      developmentTimeline: 'Development Timeline',
      additionalNotes: 'Additional Notes',
      developmentPotential: 'Development Potential',
      completionTarget: 'Completion Target',
      growthDrivers: 'Growth Drivers',
      dividendPolicy: 'Dividend Policy',
      lockupPeriod: 'Lockup Period',
      managementFee: 'Management Fee',
      carriedInterest: 'Carried Interest',
      couponRate: 'Coupon Rate',
      monthlyIncome: 'Monthly Income',
      monthlyExpenses: 'Monthly Expenses',
      additionalInfo: 'Additional Information',
      assetData: 'Asset Information',
      investorEmailId: 'Investor Email',
      investorName: 'Investor Name',
      investorAvatar: 'Investor Avatar',
      investedUnits: 'Invested Units',
      investedAmount: 'Invested Amount',
      unitPrice: 'Unit Price',
      sellUnits: 'Sell Units',
      operationCharge: 'Operation Charge',
      frequency: 'Frequency',
      contributionAmount: 'Contribution Amount'
    };

    return (
      fieldNameMappings[key] ||
      key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())
    );
  };

  // Enhanced value formatting with better date detection
  const formatValue = (key: string, value: any): string => {
    if (value === null) return 'Not provided';
    if (value === undefined) return 'Not provided';
    if (value === '') return 'Not provided';

    // Handle arrays
    if (Array.isArray(value)) {
      return value.length === 0 ? 'None' : JSON.stringify(value, null, 2);
    }

    // Handle dates - improved detection
    if (typeof value === 'string') {
      // ISO datetime format (with T and timezone)
      if (value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
        return formatDateTime(value);
      }
      // Simple date format (YYYY-MM-DD)
      else if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return formatDate(value);
      }

      // Handle currency fields
      if (
        key.toLowerCase().includes('value') ||
        key.toLowerCase().includes('price') ||
        key.toLowerCase().includes('income') ||
        key.toLowerCase().includes('expense') ||
        key.toLowerCase().includes('cash') ||
        key.toLowerCase().includes('fee') ||
        key.toLowerCase().includes('amount') ||
        key.toLowerCase().includes('charge') ||
        key === 'primary' ||
        key === 'secondary' ||
        key === 'other' ||
        key === 'financing' ||
        key === 'management' ||
        key === 'operational' ||
        key === 'fees'
      ) {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          return formatCurrency(numValue);
        }
      }

      // Handle percentage fields
      if (
        key.toLowerCase().includes('yield') ||
        key.toLowerCase().includes('rate') ||
        key.toLowerCase().includes('interest')
      ) {
        return formatPercentage(value);
      }
    }

    return String(value);
  };

  // Function to render any object as key-value pairs recursively
  const renderKeyValuePairs = (obj: any, level = 0): JSX.Element[] => {
    if (!obj || typeof obj !== 'object') {
      return [];
    }

    const items: JSX.Element[] = [];

    Object.entries(obj).forEach(([key, value]) => {
      const fieldName = formatFieldName(key);

      if (value && typeof value === 'object' && !Array.isArray(value)) {
        // Nested object - render as accordion (EXPANDED BY DEFAULT)
        items.push(
          <TableRow key={`${level}-${key}`}>
            <TableCell colSpan={2} sx={{ p: 0 }}>
              <Accordion
                elevation={0}
                sx={{ boxShadow: 'none', border: 'none' }}
                defaultExpanded={true}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    backgroundColor: level === 0 ? '#f5f5f5' : '#fafafa',
                    minHeight: 40,
                    '& .MuiAccordionSummary-content': { margin: '8px 0' }
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                    {fieldName}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <Table size="small">
                    <TableBody>{renderKeyValuePairs(value, level + 1)}</TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            </TableCell>
          </TableRow>
        );
      } else {
        // Simple value
        const displayValue = formatValue(key, value);

        items.push(
          <TableRow
            key={`${level}-${key}`}
            sx={{
              '&:nth-of-type(odd)': { backgroundColor: level === 0 ? '#fafafa' : '#ffffff' },
              '&:hover': { backgroundColor: '#e3f2fd' }
            }}
          >
            <TableCell
              sx={{
                fontWeight: 500,
                fontSize: '0.8rem',
                color: '#444',
                width: '40%',
                borderRight: '1px solid #e0e0e0',
                paddingLeft: level === 0 ? 2 : 1
              }}
            >
              {fieldName}
            </TableCell>
            <TableCell
              sx={{
                fontSize: '0.8rem',
                wordBreak: 'break-word',
                color: '#333',
                fontWeight:
                  key.toLowerCase().includes('income') ||
                  key.toLowerCase().includes('value') ||
                  key.toLowerCase().includes('price') ||
                  key.toLowerCase().includes('amount')
                    ? 500
                    : 400
              }}
            >
              {displayValue}
            </TableCell>
          </TableRow>
        );
      }
    });

    return items;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { maxHeight: '90vh' }
      }}
    >
      <DialogTitle
        sx={{
          padding: 2,
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 500, mb: 0.5 }}>
              Request Details
            </Typography>
            <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem' }}>
              Request ID: {request.requestId}
            </Typography>
          </Box>
          <Chip
            label={getStatusConfig(request.status).label}
            sx={{
              backgroundColor: getStatusConfig(request.status).bgColor,
              color: getStatusConfig(request.status).color,
              fontWeight: 500,
              fontSize: '0.7rem'
            }}
          />
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: 0,
          overflow: 'auto'
        }}
      >
        <TableContainer component={Paper} elevation={0}>
          <Table size="small" stickyHeader>
            <TableBody>{renderKeyValuePairs(request)}</TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions
        sx={{
          padding: 2,
          backgroundColor: '#f8f9fa',
          borderTop: '1px solid #e0e0e0'
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            fontSize: '0.85rem',
            textTransform: 'none',
            paddingX: 3
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequestDialog;
